/**
 * i18n configuration. English is the default language.
 */
export const DEFAULT_LOCALE = 'en' as const;

/** Request header set by proxy so root layout can render matching `<html lang>`. */
export const SITE_LOCALE_HEADER = 'x-site-locale';

export const LOCALES = ['en', 'fr', 'es', 'ru'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ru: 'Русский',
};

export const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  es: '🇪🇸',
  ru: '🇷🇺',
};

export function isValidLocale(value: string): value is Locale {
  return LOCALES.includes(value as Locale);
}

export function getLocaleOrDefault(value: string | undefined): Locale {
  if (value && isValidLocale(value)) return value as Locale;
  return DEFAULT_LOCALE;
}

/** Path for a given locale. Default locale (en) has no prefix: /casinos. Others: /ru/casinos */
export function getPathForLocale(locale: Locale, path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return p === '/' ? '/' : p;
  return `/${locale}${p === '/' ? '' : p}`;
}

/** Parse Accept-Language and return best matching locale, or default. */
export function getLocaleFromAcceptLanguage(acceptLanguage: string | null): Locale {
  if (!acceptLanguage?.trim()) return DEFAULT_LOCALE;
  const parts = acceptLanguage.split(',').map((s) => {
    const [lang, q] = s.trim().toLowerCase().split(';q=');
    const locale = (lang?.split('-')[0] ?? '').trim();
    const weight = q ? parseFloat(q) || 1 : 1;
    return { locale, weight };
  });
  parts.sort((a, b) => b.weight - a.weight);
  for (const { locale } of parts) {
    if (locale && isValidLocale(locale)) return locale as Locale;
    if (locale === 'en') return DEFAULT_LOCALE;
  }
  return DEFAULT_LOCALE;
}
